import {
  Box,
  Button,
  Card,
  CTABanner,
  FAQ,
  FAQGroup,
  Grid,
  InlineLink,
  Section,
  SectionIntro,
  Text,
} from '@primer/react-brand'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import {ArrowUpIcon} from '@primer/octicons-react'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {renderWithoutParagraphs, renderWithSpans} from '../../../utils'
import type {OutroSectionProps} from './types'
import baseStyles from '../../../styles/base.module.css'
import overviewStyles from '../../OverviewTemplate.module.css'
import {BrandedDivider} from '../../../components/BrandedDivider'

export const OutroSection = ({
  settings,
  outroCustomerStories,
  outroCtaBanner,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroIncludeResources,
  outroResourcesHeading,
  outroResources,
  outroFaqs,
  outroHeading,
  footnotes,
}: OutroSectionProps) => {
  const brand = settings?.fields.brand
  const groupsWithFaqs = outroFaqs?.filter(group =>
    group.fields.faqs.some(faq => faq.fields.question && faq.fields.answer),
  )
  const singleGroup = groupsWithFaqs && groupsWithFaqs.length === 1 ? groupsWithFaqs[0] : null

  const hasCustomerStories = outroCustomerStories && outroCustomerStories.length > 0

  if (
    !hasCustomerStories &&
    !outroCtaBanner &&
    (!outroIncludeResources || !outroResources?.length) &&
    (!groupsWithFaqs || groupsWithFaqs.length === 0)
  ) {
    return null
  }

  const storySpan = outroCustomerStories && outroCustomerStories.length > 2 ? 4 : 6

  return (
    <Box marginBlockStart={48} className={baseStyles.greenBorderBottom}>
      <Section paddingBlockStart="none" className={baseStyles.gridFrameTop} paddingBlockEnd="none" fullWidth>
        {hasCustomerStories && (
          <Box className={`${baseStyles.gridFrameBottom}`}>
            <Grid className={baseStyles.gridContent}>
              {outroCustomerStories.map(story => {
                const {logo, lead, url, title} = story.fields
                return (
                  <Grid.Column
                    key={story.sys.id}
                    span={{xsmall: 12, medium: 6, large: storySpan}}
                    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                    className="gridColumn"
                  >
                    <Box
                      className={baseStyles.gridItem}
                      padding={{narrow: 24, regular: 40}}
                      paddingBlockStart={{regular: 80}}
                      paddingBlockEnd={{regular: 80}}
                    >
                      <Card href={url} variant="minimal" fullWidth ctaVariant="arrow">
                        <Card.Image
                          src={`${logo.fields.file.url}?fm=webp&h=60`}
                          alt={logo.fields.description || title}
                          style={{height: '30px', width: 'auto', objectFit: 'contain'}}
                        />
                        <Card.Heading>{title}</Card.Heading>
                        <Card.Description>{lead}</Card.Description>
                      </Card>
                    </Box>
                  </Grid.Column>
                )
              })}
            </Grid>
          </Box>
        )}

        {outroCtaBanner && (
          <CTABanner>
            {outroCtaHeading && <CTABanner.Heading>{renderWithoutParagraphs(outroCtaHeading)}</CTABanner.Heading>}
            {outroCtaDescription && (
              <CTABanner.Description>{renderWithoutParagraphs(outroCtaDescription)}</CTABanner.Description>
            )}
            {outroCtaOne?.fields.href && outroCtaTwo?.fields.href ? (
              <CTABanner.ButtonGroup>
                <Button as="a" href={outroCtaOne.fields.href}>
                  {outroCtaOne.fields.text}
                </Button>
                <Button as="a" href={outroCtaTwo.fields.href} variant="secondary">
                  {outroCtaTwo.fields.text}
                </Button>
              </CTABanner.ButtonGroup>
            ) : outroCtaOne?.fields.href ? (
              <CTABanner.ButtonGroup>
                <Button as="a" href={outroCtaOne.fields.href}>
                  {outroCtaOne.fields.text}
                </Button>
              </CTABanner.ButtonGroup>
            ) : null}
          </CTABanner>
        )}

        {outroIncludeResources && (
          <>
            {outroResourcesHeading && (
              <Box
                paddingBlockEnd={{
                  narrow: 'spacious',
                  regular: 64,
                }}
                paddingBlockStart={{
                  narrow: 'spacious',
                  regular: 64,
                }}
              >
                <SectionIntro align="center" fullWidth>
                  <SectionIntro.Heading size="3">{renderWithoutParagraphs(outroResourcesHeading)}</SectionIntro.Heading>
                </SectionIntro>
              </Box>
            )}
            <Box className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}>
              <Grid columnGap="none" className={baseStyles.gridContent}>
                {outroResources?.map(resource => {
                  const Octicon = getPrimerIcon(resource.fields.icon)
                  return (
                    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                    <Grid.Column key={resource.sys.id} span={{medium: 4}} className="gridColumn">
                      <Box
                        className={baseStyles.gridItem}
                        padding={{narrow: 24, regular: 40, wide: 64}}
                        paddingBlockStart={{regular: 80}}
                        paddingBlockEnd={{regular: 80}}
                        borderWidth="thin"
                        borderColor="muted"
                        style={{height: '100%'}}
                      >
                        <Card
                          href={resource.fields.href}
                          variant="minimal"
                          fullWidth
                          ctaVariant="arrow"
                          ctaText={resource.fields.label}
                        >
                          {Octicon && <Card.Icon icon={<Octicon />} color="default" hasBackground />}
                          {resource.fields.eyebrow ? <Card.Label>{resource.fields.eyebrow}</Card.Label> : null}
                          <Card.Heading>
                            {renderWithoutParagraphs(resource.fields.heading)}
                            {resource.fields.alt ? <span className="sr-only"> {resource.fields.alt}</span> : null}
                          </Card.Heading>
                          <Card.Description>{renderWithoutParagraphs(resource.fields.description)}</Card.Description>
                        </Card>
                      </Box>
                    </Grid.Column>
                  )
                })}
              </Grid>
            </Box>
          </>
        )}

        {brand === 'Security' ? (
          <Box className={baseStyles.gridFrameBottom}>
            <Grid>
              <Grid.Column>
                <BrandedDivider brand={brand} />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {groupsWithFaqs && groupsWithFaqs.length > 1 && (
          <FAQGroup variant="gridline" defaultSelectedIndex={0}>
            {outroHeading && (
              <FAQGroup.Heading>
                <Box paddingBlockStart={{narrow: 'spacious', regular: 64}} paddingInlineStart="spacious">
                  {renderWithSpans(outroHeading, 'd-block')}
                </Box>
              </FAQGroup.Heading>
            )}

            {groupsWithFaqs.map(group => {
              const completeFaqs = group.fields.faqs.filter(faq => faq.fields.question && faq.fields.answer)

              return (
                <FAQ key={group.sys.id} variant="gridline">
                  {group.fields.heading && <FAQ.Heading>{renderWithoutParagraphs(group.fields.heading)}</FAQ.Heading>}
                  {completeFaqs.map((faq, index) => (
                    <FAQ.Item key={faq.sys.id} name={`faq-${group.sys.id}`} open={index === 0}>
                      <FAQ.Question>{faq.fields.question}</FAQ.Question>
                      <FAQ.Answer>
                        <Text as="div" variant="muted">
                          {documentToReactComponents(faq.fields.answer)}
                        </Text>
                      </FAQ.Answer>
                    </FAQ.Item>
                  ))}
                </FAQ>
              )
            })}
          </FAQGroup>
        )}

        {singleGroup && (
          <FAQ key={singleGroup.sys.id} variant="gridline">
            {outroHeading && (
              <FAQ.Heading>
                <Box paddingBlockStart={{narrow: 'spacious', regular: 64}} paddingInlineStart="spacious">
                  {renderWithSpans(outroHeading, 'd-block')}
                </Box>
              </FAQ.Heading>
            )}
            {singleGroup.fields.heading && (
              <FAQ.Heading>{renderWithoutParagraphs(singleGroup.fields.heading)}</FAQ.Heading>
            )}
            {singleGroup.fields.faqs
              .filter(faq => faq.fields.question && faq.fields.answer)
              .map((faq, index) => (
                <FAQ.Item key={faq.sys.id} name={`faq-${singleGroup.sys.id}`} open={index === 0}>
                  <FAQ.Question>{faq.fields.question}</FAQ.Question>
                  <FAQ.Answer>
                    <Text as="div" variant="muted">
                      {documentToReactComponents(faq.fields.answer)}
                    </Text>
                  </FAQ.Answer>
                </FAQ.Item>
              ))}
          </FAQ>
        )}

        {footnotes && footnotes.length > 0 && (
          <Grid>
            <Grid.Column>
              <Box
                paddingBlockStart={{narrow: 'spacious', regular: 64}}
                paddingBlockEnd={{narrow: 'spacious', regular: 64}}
              >
                <Text as="div" size="100" variant="muted">
                  <ol>
                    {footnotes.map((footnote, index) => (
                      <li key={footnote.id} id={`footnote-${index + 1}`} className={overviewStyles.footnotesItem}>
                        {renderWithoutParagraphs(footnote.content)}{' '}
                        <InlineLink
                          href="#pricing-section"
                          aria-label={`Back to pricing section (footnote ${index + 1})`}
                        >
                          <ArrowUpIcon size={12} />
                        </InlineLink>
                      </li>
                    ))}
                  </ol>
                </Text>
              </Box>
            </Grid.Column>
          </Grid>
        )}
      </Section>
    </Box>
  )
}
